import { useEffect } from 'react'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import {
  setMeIntegrationEnabled,
  setMeIntegrationSettings,
} from '@sportninja/common/actions/account'
import { useDispatch } from 'src/hooks/useRedux'
import { useIntegrationsReducer } from './useIntegrationsReducer'
import {
  fromHockeyUSASeasonChoice,
  toHockeyUSASeasonChoice,
} from './hockeyUSASeason'

/**
 * Interface for useHockeyUSAIntegration hook parameters
 */
interface HockeyUSAIntegrationParams {
  accountUid?: string
  integrations?: {
    daysmart?: {
      is_connected: boolean
    }
    usahockey?: {
      is_enabled: boolean
      previous_season_cutoff?: string | null
    }
  }
}

/**
 * Interface for useHockeyUSAIntegration hook return value
 */
interface HockeyUSAIntegrationReturn {
  // State values
  hockeyUSAApiKey: string
  hockeyUSASecretKey: string
  isHockeyUSAConnected: boolean
  isHockeyUSAEnabled: boolean
  showHockeyUSAWarning: boolean
  pendingHockeyUSAEnabled: boolean
  /** The option the season select should show, not the raw API value. */
  hockeyUSASeasonChoice: string
  isSavingHockeyUSASeasonCutoff: boolean

  // Actions
  actions: ReturnType<typeof useIntegrationsReducer>['actions']

  // Functions
  handleHockeyUSAToggle: (enabled: boolean) => Promise<void>
  updateHockeyUSAStatus: (enabled: boolean) => Promise<void>
  confirmHockeyUSAEnable: () => Promise<void>
  cancelHockeyUSAEnable: () => void
  handleHockeyUSASeasonChoice: (choice: string) => Promise<void>
}

/**
 * Custom hook for managing USA Hockey integration functionality
 *
 * This hook encapsulates all USA Hockey-related logic including:
 * - Fetching USA Hockey API keys and enabled status from the backend
 * - Toggling USA Hockey integration on/off with proper confirmation flow
 * - Managing the warning modal state for cost confirmation
 * - Handling the enable/disable workflow with proper error handling
 *
 * @param params - Configuration object containing account UID
 * @returns Object containing state values, actions, and utility functions for USA Hockey integration
 *
 * @example
 * ```typescript
 * const hockeyUSAIntegration = useHockeyUSAIntegration({
 *   accountUid: account?.uid
 * })
 *
 * // Access state
 * const { isHockeyUSAEnabled, showHockeyUSAWarning } = hockeyUSAIntegration
 *
 * // Perform actions
 * await hockeyUSAIntegration.handleHockeyUSAToggle(true)
 * hockeyUSAIntegration.cancelHockeyUSAEnable()
 * ```
 */
export const useHockeyUSAIntegration = ({
  accountUid,
  integrations,
}: HockeyUSAIntegrationParams): HockeyUSAIntegrationReturn => {
  console.log(
    '🔄 useHockeyUSAIntegration: Initializing USA Hockey integration hook'
  )

  const dispatch = useDispatch()
  const { state, actions } = useIntegrationsReducer()
  const {
    hockeyUSAApiKey,
    hockeyUSASecretKey,
    isHockeyUSAConnected,
    isHockeyUSAEnabled,
    showHockeyUSAWarning,
    pendingHockeyUSAEnabled,
    hockeyUSAPreviousSeasonCutoff,
    isSavingHockeyUSASeasonCutoff,
  } = state

  // Set USA Hockey connection status from integrations data
  useEffect(() => {
    if (!accountUid) {
      return
    }

    // Set connection and enabled status from integrations data
    if (integrations?.usahockey?.is_enabled) {
      actions.setIsHockeyUSAConnected(true)
      actions.setIsHockeyUSAEnabled(true)
      console.log('✅ useHockeyUSAIntegration: USA Hockey is enabled')
    } else {
      actions.setIsHockeyUSAConnected(false)
      actions.setIsHockeyUSAEnabled(false)
      console.log('⚠️ useHockeyUSAIntegration: USA Hockey is not enabled')
    }
    // Only depend on accountUid and integrations to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, integrations?.usahockey?.is_enabled])

  // SN-8202. Seed the season policy from the account. Undefined means the account has not
  // been loaded yet, and must not be mistaken for "cleared" -- only an explicit null is that.
  useEffect(() => {
    if (!accountUid) {
      return
    }

    actions.setHockeyUSAPreviousSeasonCutoff(
      integrations?.usahockey?.previous_season_cutoff ?? null
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, integrations?.usahockey?.previous_season_cutoff])

  /**
   * Updates USA Hockey integration status on the server
   * @param enabled - Whether to enable or disable the integration
   */
  const updateHockeyUSAStatus = async (enabled: boolean) => {
    console.log('🔄 useHockeyUSAIntegration: Updating USA Hockey status', {
      enabled,
    })
    try {
      // Use different endpoints for enable and disable
      const endpoint = enabled
        ? `/account/${accountUid}/integrations/usahockey/enable`
        : `/account/${accountUid}/integrations/usahockey/disable`

      await req(endpoint, {
        method: 'PUT',
      })

      actions.setIsHockeyUSAEnabled(enabled)
      // Persist to the account slice too: this hook's state is local and
      // resets when the card unmounts (e.g. switching settings tabs), which
      // otherwise re-seeded a stale value until a full page reload.
      dispatch(setMeIntegrationEnabled({ key: 'usahockey', enabled }))
      console.log(
        '✅ useHockeyUSAIntegration: USA Hockey status updated successfully'
      )
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      console.error(
        '🚨 useHockeyUSAIntegration: Failed to update USA Hockey status',
        errorMessage
      )
      // Reset switch state on error
      actions.setIsHockeyUSAEnabled(!enabled)
    }
  }

  /**
   * Handles the USA Hockey integration toggle with proper confirmation flow
   * - For enabling: Shows warning modal before proceeding
   * - For disabling: Immediately disables without confirmation
   * @param enabled - Whether to enable or disable the integration
   */
  const handleHockeyUSAToggle = async (enabled: boolean) => {
    console.log('🔄 useHockeyUSAIntegration: Toggling USA Hockey integration', {
      enabled,
    })

    if (enabled) {
      // Show warning modal before enabling
      actions.setPendingHockeyUSAEnabled(true)
      actions.setShowHockeyUSAWarning(true)
    } else {
      // Disable immediately without warning
      await updateHockeyUSAStatus(false)
    }
  }

  /**
   * Confirms the USA Hockey integration enable action
   * Called when user confirms they want to enable the integration despite the cost warning
   */
  const confirmHockeyUSAEnable = async () => {
    console.log('🔄 useHockeyUSAIntegration: Confirming USA Hockey enable')
    actions.setShowHockeyUSAWarning(false)
    await updateHockeyUSAStatus(pendingHockeyUSAEnabled)
    actions.setPendingHockeyUSAEnabled(false)
  }

  /**
   * Cancels the USA Hockey integration enable action
   * Called when user decides not to enable the integration after seeing the cost warning
   */
  const cancelHockeyUSAEnable = () => {
    console.log('🔄 useHockeyUSAIntegration: Cancelling USA Hockey enable')
    actions.resetHockeyUSAWarningState()
    // Switch state will remain unchanged (stays false)
  }

  /**
   * SN-8202. Saves how long the account keeps accepting a previous-season number.
   *
   * Optimistic: the select shows the new choice straight away and rolls back if the save
   * fails, so a slow round trip does not leave the control looking stuck on the old value.
   */
  const handleHockeyUSASeasonChoice = async (choice: string) => {
    const previous = hockeyUSAPreviousSeasonCutoff
    const cutoff = fromHockeyUSASeasonChoice(choice)

    if (cutoff === previous) {
      return
    }

    actions.setHockeyUSAPreviousSeasonCutoff(cutoff)
    actions.setIsSavingHockeyUSASeasonCutoff(true)

    try {
      await req(`/account/${accountUid}/integrations/usahockey/settings`, {
        method: 'PUT',
        body: JSON.stringify({ previous_season_cutoff: cutoff }),
      })

      // Persist to the account slice for the same reason the toggle does: this hook's state
      // is local and resets when the card unmounts.
      dispatch(
        setMeIntegrationSettings({
          key: 'usahockey',
          settings: { previous_season_cutoff: cutoff },
        })
      )
      console.log(
        '✅ useHockeyUSAIntegration: USA Hockey season policy updated',
        cutoff
      )
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      console.error(
        '🚨 useHockeyUSAIntegration: Failed to update USA Hockey season policy',
        errorMessage
      )
      actions.setHockeyUSAPreviousSeasonCutoff(previous)
    } finally {
      actions.setIsSavingHockeyUSASeasonCutoff(false)
    }
  }

  return {
    // State values
    hockeyUSAApiKey,
    hockeyUSASecretKey,
    isHockeyUSAConnected,
    isHockeyUSAEnabled,
    showHockeyUSAWarning,
    pendingHockeyUSAEnabled,
    hockeyUSASeasonChoice: toHockeyUSASeasonChoice(
      hockeyUSAPreviousSeasonCutoff
    ),
    isSavingHockeyUSASeasonCutoff,

    // Actions
    actions,

    // Functions
    handleHockeyUSAToggle,
    updateHockeyUSAStatus,
    confirmHockeyUSAEnable,
    cancelHockeyUSAEnable,
    handleHockeyUSASeasonChoice,
  }
}
